import { ICreateQuestionnaireInput } from 'graphql/secure/mutations/types/questionnaire-mutation.types';
import {
  EQuestionType,
  IQuestionnaireCompanySection,
} from 'FeatureModules/Questionnaire/types/questionnaire.types';
import * as Yup from 'yup';

export const createQuestionnaireSchema = Yup.object().shape({
  title: Yup.string().default('').required('Title is required'),
  templateId: Yup.string().default('').required('Template is required'),
  year: Yup.number()
    .default(new Date().getFullYear())
    .min(1900, 'Year must be after 1900')
    .max(new Date().getFullYear() + 1, 'Year must be in the current year or earlier')
    .required('Year is required'),
  externalId: Yup.string().default('').nullable(),
}) as Yup.ObjectSchema<ICreateQuestionnaireInput>;

export const dynamicQuestionnaireSchema = (sections: IQuestionnaireCompanySection[]) =>
  Yup.object().shape({
    sections: Yup.object().shape({
      ...sections.reduce(
        (prev, section) => ({
          ...prev,
          [section.id]: Yup.object().shape({
            questions: Yup.object().shape({
              ...section.questions.reduce(
                (prevQuestions, question) => ({
                  ...prevQuestions,
                  [question.id]: Yup.object().shape({
                    ...(question.questionType === EQuestionType.SHORT_FREE_TEXT ||
                    question.questionType === EQuestionType.LONG_FREE_TEXT
                      ? {
                          answerText: question.isRequired
                            ? Yup.string()
                                .nullable()
                                .default('')
                                .required('This field is required. Please enter your answer.')
                            : Yup.string().nullable().default('').optional(),
                        }
                      : {}),

                    ...(question.questionType === EQuestionType.SINGLE_SELECT ||
                    question.questionType === EQuestionType.SINGLE_SELECT_DROP_DOWN
                      ? {
                          selectedOptionId: question.isRequired
                            ? Yup.string()
                                .nullable()
                                .required('This field is required. Please select an option.')
                            : Yup.string().nullable().optional(),
                        }
                      : {}),

                    ...(question.questionType === EQuestionType.MULTI_SELECT ||
                    question.questionType === EQuestionType.MULTI_SELECT_DROP_DOWN
                      ? {
                          selectedOptionIds: question.isRequired
                            ? Yup.array()
                                .of(Yup.string())
                                .nullable()
                                .required(
                                  'This field is required. Please select one or multiple options.'
                                )
                                .min(1)
                                .default([])
                            : Yup.array().of(Yup.string()).nullable().default([]),
                        }
                      : {}),

                    ...(question.questionType === EQuestionType.DOCUMENTS
                      ? {
                          documents: question.isRequired
                            ? Yup.array()
                                .of(
                                  Yup.object().shape({
                                    id: Yup.string(),
                                    title: Yup.string(),
                                    inUploadContext: Yup.boolean(),
                                  })
                                )
                                .nullable()
                                .required(
                                  'This field is required. Please upload at least one document.'
                                )
                                .min(1)
                                .default([])
                            : Yup.array().of(Yup.string()).nullable().default([]),
                        }
                      : {}),

                    ...(question.questionType !== EQuestionType.SHORT_FREE_TEXT &&
                    question.questionType !== EQuestionType.LONG_FREE_TEXT
                      ? {
                          commentText: Yup.string().when(
                            question.questionType === EQuestionType.SINGLE_SELECT ||
                              question.questionType === EQuestionType.SINGLE_SELECT_DROP_DOWN
                              ? 'selectedOptionId'
                              : 'selectedOptionIds',
                            {
                              is: (value: string | string[]) =>
                                question.options?.find(({ id }) =>
                                  Array.isArray(value) ? value.includes(id) : id === value
                                )?.isCommentRequired,
                              then: Yup.string()
                                .nullable()
                                .required(
                                  'The option you selected requires a comment. Please enter your comment.'
                                ),
                              otherwise: Yup.string().nullable().optional(),
                            }
                          ),
                        }
                      : {}),
                  }),
                }),
                {}
              ),
            }),
          }),
        }),
        {}
      ),
    }),
  });
